import { Phone, MapPin, MessageCircle, Clock, Facebook, Instagram, Youtube, Users } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const rescuers = [
    {
      name: 'Alhan Nawaz',
      phone: '+91 8310755854',
      whatsapp: '918310755854',
      address: 'Babusapalya, Bangalore - 560043',
    },
    {
      name: 'Mohan K',
      phone: '+91 9845080903',
      whatsapp: '919845080903',
      address: 'Ulsoor, Bangalore - 560008',
    },
  ];

  const serviceAreas = [
    'KR Puram', 'Hebbal', 'Kempegowda Airport', 'Bangalore Central',
    'Babusapalya', 'Kalyan Nagar', 'Hennur', 'Banaswadi',
    'Ramamurthy Nagar', 'Kasturi Nagar', 'HRBR Layout', 'Kammanahalli',
    'Lingarajapuram', 'RT Nagar', 'Sanjaynagar', 'Nagawara',
    'Thanisandra', 'Sahakarnagar', 'Yelahanka', 'Jakkur'
  ];

  const quickLinks = [
    { href: '#services', label: 'Services' },
    { href: '#gallery', label: 'Gallery' },
    { href: '#testimonials', label: 'Testimonials' },
    { href: '#faq', label: 'FAQ' },
    { href: '#emergency-tips', label: 'Emergency Tips' },
  ];

  return (
    <footer 
      id="contact"
      className="bg-[#1A2C20] pt-20 pb-8"
      data-testid="footer-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <a href="#" className="flex items-center gap-3 mb-6">
              <img 
                src="/logo.png" 
                alt="Snake Rescue Bangalore Logo" 
                className="h-16 w-auto"
              />
            </a>
            <p className="text-[#9CA89F] mb-6 leading-relaxed">
              Professional snake rescue and relocation services in Bangalore. 
              Protecting both humans and wildlife with over 10,000 rescues completed.
            </p>
            <div className="flex items-center gap-4">
              <a 
                href="#" 
                className="w-10 h-10 bg-[#2A4B35] rounded-full flex items-center justify-center hover:bg-[#25D366]/20 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5 text-[#9CA89F]" />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 bg-[#2A4B35] rounded-full flex items-center justify-center hover:bg-[#25D366]/20 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5 text-[#9CA89F]" />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 bg-[#2A4B35] rounded-full flex items-center justify-center hover:bg-[#25D366]/20 transition-colors"
                aria-label="Youtube"
              >
                <Youtube className="w-5 h-5 text-[#9CA89F]" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 
              className="font-bold text-[#F5F3ED] mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href}
                    className="text-[#9CA89F] hover:text-[#25D366] transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h4 
              className="font-bold text-[#F5F3ED] mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Service Areas
            </h4>
            <p className="text-[#25D366] text-sm mb-3 font-medium">
              10km radius from Bangalore 560043
            </p>
            <p className="text-[#9CA89F] text-xs mb-3">
              KR Puram → Hebbal | Airport → Central
            </p>
            <ul className="grid grid-cols-2 gap-1">
              {serviceAreas.map((area, index) => (
                <li key={index} className="text-[#9CA89F] text-xs">
                  {area}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info - Both Rescuers */}
          <div>
            <h4 
              className="font-bold text-[#F5F3ED] mb-6 flex items-center gap-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              <Users className="w-4 h-4" />
              Our Team
            </h4>
            <div className="space-y-6">
              {rescuers.map((rescuer, index) => (
                <div key={index} className="space-y-2">
                  <p className="text-[#25D366] font-semibold text-sm">{rescuer.name}</p>
                  <a 
                    href={`tel:${rescuer.phone.replace(/\s/g, '')}`}
                    className="flex items-center gap-2 text-[#9CA89F] hover:text-[#25D366] transition-colors text-sm"
                    data-testid={`footer-phone-${index + 1}`}
                  >
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    <span>{rescuer.phone}</span>
                  </a>
                  <a 
                    href={`https://wa.me/${rescuer.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[#9CA89F] hover:text-[#25D366] transition-colors text-sm"
                    data-testid={`footer-whatsapp-${index + 1}`}
                  >
                    <MessageCircle className="w-4 h-4 flex-shrink-0" />
                    <span>WhatsApp</span>
                  </a>
                  <p className="flex items-start gap-2 text-[#9CA89F] text-sm">
                    <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{rescuer.address}</span>
                  </p>
                </div>
              ))}
              <div className="flex items-center gap-2 text-[#9CA89F] text-sm pt-2">
                <Clock className="w-4 h-4 flex-shrink-0" />
                <span>24/7 Emergency Service</span>
              </div>
            </div>
          </div>
        </div>

        {/* Emergency Banner */}
        <div className="bg-[#D9534F] rounded-2xl p-6 md:p-8 mb-12">
          <div className="flex flex-col items-center text-center gap-6">
            <div>
              <h3 
                className="text-xl md:text-2xl font-bold text-white mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Emergency Snake Rescue?
              </h3>
              <p className="text-white/80">
                Don't panic. Call any of our experts for immediate assistance.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <a 
                href="tel:+918310755854"
                className="flex items-center gap-3 bg-white text-[#D9534F] font-bold px-6 py-3 rounded-full hover:bg-[#F5F3ED] transition-colors"
                data-testid="footer-emergency-btn-1"
              >
                <Phone className="w-5 h-5" />
                Alhan: +91 8310755854
              </a>
              <a 
                href="tel:+919845080903"
                className="flex items-center gap-3 bg-white text-[#D9534F] font-bold px-6 py-3 rounded-full hover:bg-[#F5F3ED] transition-colors"
                data-testid="footer-emergency-btn-2"
              >
                <Phone className="w-5 h-5" />
                Mohan: +91 9845080903
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#2A4B35] pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-[#9CA89F] text-sm">
            <p>&copy; {currentYear} Snake Rescue Bangalore Team. All rights reserved.</p>
            <p>Protecting Bangalore, one rescue at a time.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
